import {useEffect, useRef} from 'react';
import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {getStore} from 'src/v2/state/store';
import {useIntl} from '@sail/intl';
import {usePrepareSessionForPayment} from 'src/v2/components/pay/usePrepareSessionForPayment';
import {useConfirmSessionForPayment} from 'src/v2/components/pay/useConfirmSessionForPayment';
import type {SupportedCurrency} from 'src/v2/state/types';
import {EVM_NETWORKS} from 'src/types';
import {convertWalletTypeToApiType} from 'src/utils/convertWalletTypeToApiType';
import {
  getEvmTransactionData,
  getSolanaTransactionData,
} from 'src/v2/components/pay/getTransactionData';
import {getSourceTokenAccountAddress} from 'src/utils/solanaHelpers';
import {useSignPermitMessage} from 'src/v2/components/pay/useSignPermitMessage';
import {syncWalletToSession} from 'src/v2/components/pay/syncWalletToSession';
import {useUpdateSessionOnWalletConnect} from 'src/v2/components/walletConnect/useUpdateSessionOnWalletConnect';
import {
  handleError,
  clearErrorBanner,
  CustomDisplayError,
} from 'src/v2/components/error/handleError';
import {ApolloError} from '@sail/data';
import {useLogger} from 'src/utils/logger';
import {apiAmountToAssetAmount} from 'src/utils/helpers';
import {MAX_UINT_256_VALUE} from 'src/utils/constants';
import {useResyncSessionState} from 'src/v2/components/pay/useResyncSessionState';
import {useSessionContext} from 'src/state/usePayinSessionContext';

const GASLESS_INVALID_SIGNATURE_ERROR =
  'crypto_payin_setup_transaction_invalid_signature';

export const useHandlePayment = () => {
  const intl = useIntl();
  const walletClient = useWalletConnectionClient();
  const store = getStore();
  const prepareSessionForPayment = usePrepareSessionForPayment();
  const confirmSessionForPayment = useConfirmSessionForPayment();
  const {signPermitMessage} = useSignPermitMessage();
  const [updateSession] = useUpdateSessionOnWalletConnect();
  const logger = useLogger();
  const resyncSessionState = useResyncSessionState();
  const {sessionId} = useSessionContext();

  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const amount = store.useSelector((state) => state.paymentDetails.amount);
  const paymentCurrency = store.useSelector(
    (state) => state.paymentDetails.currency,
  );
  const depositAddresses = store.useSelector((state) => state.depositAddresses);
  const walletSessions = store.useSelector((state) => state.walletSessions);
  const setupFutureUsage = store.useSelector((state) => state.setupFutureUsage);
  const enableGaslessV2 = store.useSelector(
    (state) => state.featureFlags.enable_gasless_v2,
  );
  const spenderContractAddresses = store.useSelector(
    (state) => state.spenderContractAddresses,
  );

  // Resets on wallet or network switch so gasless is retried.
  const gaslessFailedRef = useRef(false);

  useEffect(() => {
    gaslessFailedRef.current = false;
  }, [activeWallet?.address, activeWallet?.network]);

  const handlePayment = async ({
    selectedCurrency,
    setIsProcessing,
    onConfirmSuccess,
  }: {
    selectedCurrency: SupportedCurrency;
    setIsProcessing: (processing: boolean) => void;
    onConfirmSuccess: () => void;
  }): Promise<void> => {
    if (!activeWallet) {
      return;
    }

    clearErrorBanner();

    logger.log('crypto_payin_session.pay_button_pressed', {
      currency: selectedCurrency.id,
      provider: activeWallet.type,
      address: activeWallet.address,
      wallet_key: activeWallet.key,
    });

    const isEvm = EVM_NETWORKS.includes(selectedCurrency.currencyNetwork);
    const isGasless =
      isEvm &&
      enableGaslessV2 &&
      !activeWallet.isSmartContractWallet &&
      !!spenderContractAddresses?.[
        `${selectedCurrency.assetCode}.${selectedCurrency.currencyNetwork}`
      ] &&
      !gaslessFailedRef.current;

    try {
      setIsProcessing(true);

      await syncWalletToSession(activeWallet, walletSessions, updateSession);
      const walletProvider = convertWalletTypeToApiType(activeWallet.type);

      const {transactionDetails} = await prepareSessionForPayment({
        selectedCurrency: selectedCurrency.assetCode,
        walletAddress: activeWallet.address,
        network: selectedCurrency.currencyNetwork,
        walletProvider,
      });

      const depositAmount = amount;

      if (isEvm) {
        if (isGasless || setupFutureUsage) {
          store.setValue((state) => {
            state.overlay = 'CONFIRM_PAYMENT_WITH_FUTURE_USAGE';
          });

          // For SFU payments, request the maximum allowance because the payment amount can change in the future
          const permitValue = setupFutureUsage
            ? MAX_UINT_256_VALUE
            : apiAmountToAssetAmount(
                amount,
                paymentCurrency,
                selectedCurrency.currencyMinorUnits,
              );

          const signature = await signPermitMessage({
            asset: selectedCurrency,
            permitValue,
          });

          store.setValue((state) => {
            state.overlay = null;
          });

          await confirmSessionForPayment({
            signedTransactionData: signature,
            selectedCurrency: selectedCurrency.assetCode,
            walletAddress: activeWallet.address,
            network: selectedCurrency.currencyNetwork,
            walletProvider,
            walletKey: activeWallet.key,
          });
        } else {
          const transactionData = getEvmTransactionData({
            selectedCurrency,
            amount: depositAmount,
            depositAddresses,
            memoMessage: sessionId,
            sourceCurrency: paymentCurrency,
          });

          store.setValue((state) => {
            state.overlay = 'CONFIRM_PAYMENT';
          });

          // Transaction hash is guaranteed to be a valid transaction hash
          const transactionHash = await walletClient.evmSendTransaction(
            transactionData,
          );

          store.setValue((state) => {
            state.overlay = null;
          });

          await confirmSessionForPayment({
            selectedCurrency: selectedCurrency.assetCode,
            walletAddress: activeWallet.address,
            network: selectedCurrency.currencyNetwork,
            walletProvider,
            walletKey: activeWallet.key,
            transactionHash,
          });
        }
      } else {
        const transactionData = getSolanaTransactionData({
          selectedCurrency,
          amount: depositAmount,
          depositAddresses,
          memoMessage: sessionId,
          transactionDetails,
          sourceCurrency: paymentCurrency,
        });

        store.setValue((state) => {
          state.overlay = 'CONFIRM_PAYMENT';
        });

        const transactionHash = await walletClient.solanaSignTransaction(
          transactionData,
        );

        store.setValue((state) => {
          state.overlay = null;
        });

        const solanaSourceTokenAccountAddress = activeWallet.address
          ? getSourceTokenAccountAddress(
              selectedCurrency.contractAddress,
              activeWallet.address,
              selectedCurrency.assetCode.toString(),
            )
          : undefined;

        await confirmSessionForPayment({
          signedTransactionData: transactionHash,
          solanaSourceTokenAccountAddress:
            solanaSourceTokenAccountAddress?.toBase58(),
          selectedCurrency: selectedCurrency.assetCode,
          walletAddress: activeWallet.address,
          network: selectedCurrency.currencyNetwork,
          walletProvider,
          walletKey: activeWallet.key,
        });
      }
      onConfirmSuccess();
    } catch (error) {
      const isGaslessFallbackError =
        isGasless &&
        !setupFutureUsage &&
        error instanceof ApolloError &&
        // @ts-expect-error - code is not typed on GraphQLError
        error.graphQLErrors[0]?.code === GASLESS_INVALID_SIGNATURE_ERROR;

      let handledError = error;
      if (isGaslessFallbackError) {
        gaslessFailedRef.current = true;
        handledError = new CustomDisplayError('gasless_fallback_error');
      }

      const context = setupFutureUsage || isGasless ? 'setup' : 'transfer';
      await resyncSessionState();
      handleError({error: handledError, intl, context});
    } finally {
      setIsProcessing(false);
    }
  };

  return handlePayment;
};
